/**
 * @ankr/glass/react — the React binding. OPTIONAL, and that is the point.
 *
 * Everything in this package works without React: the CSS is the system, and the twelve plain-HTML
 * mari8x surfaces adopt it by importing two stylesheets. This file exists so a React product does not
 * have to hand-write the same five provenance branches in every component — not because React is the
 * entry point. A design system whose only door is a framework cannot be adopted incrementally, and
 * incremental is the only kind of adoption this codebase can afford.
 *
 * React is a peerDependency and an optional one. Importing this file is what pulls it in.
 */

import { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect, isValidElement } from 'react';
import { STATES, normalise, validate } from '@ankr/provenance';
import { requestGateDecision, showGateReceipt } from './pause.js';

/* ── ONE VALUE ────────────────────────────────────────────────────────────────────────────────
 * The whole product rests on a reader being able to see HOW the system knows something. This
 * component is where that promise is kept, so it is deliberately the smallest thing here.
 *
 * It renders the CONTRACT, not a guess about a payload — `normalise()` still accepts the legacy
 * `{kind:'null'}` the engines return today, so adoption needs no flag day.
 */
export function Value({ of, empty = '—', className = '' }) {
  const e = normalise(of);

  if (e.state === STATES.REFUSED)
    return (
      <span className={`g-val ${className}`}>
        <span className="g-chip g-chip-null" title={e.reason}>refused</span>
        {e.reason ? <span className="gl-why">{e.reason}</span> : null}
      </span>
    );

  if (e.state === STATES.FAILED)
    return (
      <span className={`g-val ${className}`}>
        {/* An outage is not an abstention. Same absence on screen, different fact, different chip. */}
        <span className="g-chip g-chip-warn">failed</span>
        <span className="gl-why">{e.reason}</span>
      </span>
    );

  if (e.state === STATES.GATED)
    return (
      <span className={`g-val ${className}`}>
        {/* Its own chip, and deliberately not the refusal chip: "refused" says the system does not
            hold this, and it does. Wearing the abstention's clothes would make a fee look like a gap
            in the corpus and quietly corrupt every coverage figure a reader takes from this page. */}
        <span className="g-chip g-chip-gated" title={e.reason}>enrol to read</span>
        <span className="gl-why">{e.reason}</span>
      </span>
    );

  if (e.state === STATES.ABSENT)
    return <span className={`gl-absent ${className}`} aria-label="no value">{empty}</span>;

  const v = e.value;
  /* A CITED VALUE MAY BE A NODE, not only a scalar (2026-08-16). A quoted passage is prose with
     structure in it — commands, tables, lists — and `String(v)` flattened all of it into one line
     while `typeof v === 'object'` would have JSON.stringify'd a React element onto the page. The
     caller renders the structure and this keeps the provenance chip, so a passage cannot gain
     formatting by losing its citation. */
  if (isValidElement(v)) return (
    /* A div, not the usual span: the node it wraps contains block elements — <pre>, <table>, <ul> —
       and a <pre> inside a <span> is invalid markup that browsers recover from by silently
       restructuring the DOM around it. The chip markup is identical to the scalar branch below, on
       purpose, so the two cannot drift into looking like different kinds of claim. */
    <div className={`g-val g-val-node ${className}`}>
      {v}
      {e.source ? <span className="g-chip g-chip-cited" title={String(e.source)}>cited</span> : null}
    </div>
  );
  const body = typeof v === 'boolean' ? (v ? '✓' : '✗')
    : typeof v === 'number' ? <span className="g-mono">{v.toLocaleString()}</span>
    : Array.isArray(v) ? v.join(' · ')
    : typeof v === 'object' && v !== null ? <span className="g-mono">{JSON.stringify(v)}</span>
    : String(v ?? '');

  return (
    <span className={`g-val ${className}`}>
      {body}
      {/* The source chip goes on EVERY value type. Attaching it only to numbers is how a cited
          string — the commonest kind, a quoted rule — silently loses its citation. */}
      {e.source ? <span className="g-chip g-chip-cited" title={e.source}>cited</span> : null}
    </span>
  );
}

/* ── AUTO-TABLE ───────────────────────────────────────────────────────────────────────────────
 * Columns derived from the union of keys across ALL rows, never just the first — a first row
 * missing a field would otherwise drop that column for every row that has it. Below 640px the CSS
 * turns each row into a card; there is one DOM and no resize listener to get wrong.
 */
export function AutoTable({ widget = {}, rows = [], columns = null, empty = 'Nothing to show' }) {
  const list = Array.isArray(rows) ? rows : rows ? [rows] : [];
  const cols = useMemo(
    () => columns || [...new Set(list.flatMap((r) => Object.keys(r || {})))],
    [columns, list]
  );
  if (!list.length) return <div className="gl-empty g-body">{empty}</div>;

  return (
    <div className="gl-table-wrap">
      {widget.slug ? <div className="g-label gl-cap">{widget.slug}</div> : null}
      <table className="gl-table">
        <thead>
          <tr>{cols.map((c) => <th key={c} scope="col">{c.replace(/_/g, ' ')}</th>)}</tr>
        </thead>
        <tbody>
          {list.map((r, i) => (
            <tr key={r?.id ?? i}>
              {cols.map((c) => (
                <td key={c} data-col={c.replace(/_/g, ' ')}><Value of={r?.[c]} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="gl-foot g-dim">
        {list.length.toLocaleString()} {list.length === 1 ? 'row' : 'rows'}
        {widget.wire?.kind ? ` · via ${widget.wire.kind}` : ''}
      </div>
    </div>
  );
}

/* ── THE PAUSE ────────────────────────────────────────────────────────────────────────────────
 * A governed act is not a button that fires. It states a CONSEQUENCE and hands the decision to a
 * human, and the human's answer — either answer — is the result.
 */
const GateCtx = createContext(null);

export function GateProvider({ user = {}, onProof = null, children }) {
  const decide = useCallback(async (opts) => {
    const d = await requestGateDecision({ ...opts, user });
    // The caller owns the proof transaction: this layer must not decide when a signature is written.
    if (d.decision === 'approved' && onProof) {
      const proof = await onProof(d);
      if (proof) showGateReceipt({ decision: d.decision, ...proof });
    }
    return d;
  }, [user, onProof]);
  return <GateCtx.Provider value={decide}>{children}</GateCtx.Provider>;
}

/** Returns a function that asks the human and resolves to their decision. */
export function useGate() {
  const decide = useContext(GateCtx);
  if (!decide) throw new Error(
    '<GateProvider> is missing. A governed act must not run without somewhere for a human to refuse ' +
    'it — falling back to "just do it" would be the whole discipline quietly removed.');
  return decide;
}

export function ActionCard({ widget = {}, consequence = '', irreversible = '', disabled = false,
                             onRun = null, widgetOutput = null }) {
  const decide = useGate();
  const [busy, setBusy] = useState(false);
  const name = widget.slug ? widget.slug.replace(/[-_]/g, ' ') : 'Run this action';
  const paused = widget.pause === true;

  const run = async () => {
    setBusy(true);
    try {
      const d = await decide({ capability: widget.slug, what: name, consequence, irreversible,
                               widgetOutput, requireReason: paused });
      // Rejection is a correct outcome, not an error path. Nothing is thrown and nothing is logged
      // as a failure — the person was asked and they answered.
      if (d.decision === 'approved' && onRun) await onRun(d);
    } finally { setBusy(false); }
  };

  return (
    <div className="g-glass gl-card">
      <div className="gl-card-head">
        <div className="g-label">{paused ? 'Governed — needs your decision' : 'Governed action'}</div>
        <div className="g-h3 gl-card-name">{name}</div>
      </div>
      {consequence ? <p className="g-body gl-card-what">{consequence}</p> : null}
      {irreversible ? <div className="gl-irrev">⚠ {irreversible}</div> : null}
      <div className="gl-card-foot">
        <button className="g-btn gl-run" disabled={disabled || busy} onClick={run}>
          {busy ? 'Waiting for you…' : paused ? 'Review and decide' : 'Run'}
        </button>
        <div className="gl-card-meta g-dim">
          {widget.proof_capture ? <span className="g-chip g-chip-cited">signed on decision</span> : null}
          {widget.wire?.kind ? <span className="g-mono">{widget.wire.kind}</span> : null}
        </div>
      </div>
    </div>
  );
}

/** Render by the compiler's layout name, so nothing here chooses. An unknown layout is a FAULT. */
export function Widget({ widget, data, ...rest }) {
  if (widget?.layout === 'action-card') return <ActionCard widget={widget} {...rest} />;
  if (widget?.layout === 'auto-table') return <AutoTable widget={widget} rows={data} {...rest} />;
  return (
    <div className="gl-empty g-body">
      No renderer for layout <span className="g-mono">{widget?.layout || 'undefined'}</span> —
      widget-forge emitted a layout this version of @ankr/glass does not implement.
    </div>
  );
}

/* ── THEME ────────────────────────────────────────────────────────────────────────────────────
 * No stored choice means follow the device. Forcing dark on someone whose phone asked for light
 * overrides a statement they already made.
 */
export function useTheme() {
  const [theme, set] = useState(() => {
    try { const s = localStorage.getItem('ankr-theme'); if (s) return s; } catch {}
    return matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('ankr-theme', theme); } catch {}
  }, [theme]);
  return [theme, set];
}

/* ── ONLINE ───────────────────────────────────────────────────────────────────────────────────
 * `navigator.onLine` answers "is there a network interface", not "can I reach the server", so it is
 * reported as a HINT and never as truth. A surface that greys itself out on a false negative is
 * worse than one that simply tries and reports what happened.
 */
export function useOnlineHint() {
  const [on, set] = useState(() => navigator.onLine !== false);
  useEffect(() => {
    const up = () => set(true), down = () => set(false);
    addEventListener('online', up); addEventListener('offline', down);
    return () => { removeEventListener('online', up); removeEventListener('offline', down); };
  }, []);
  return on;
}

/**
 * useWidgetData — fetch through the contract.
 *
 * Every network answer is normalised into a provenance envelope, so a component never sees a raw
 * rejection: a dead server is `failed`, a declined answer is `refused`, and both render as themselves.
 * In DEV, payloads are validated and faults are logged loudly — a contract nobody checks is a
 * convention with better documentation.
 */
/**
 * `offline` — an OPTIONAL function that computes this widget's payload without a network.
 *
 * Some widgets are a view onto data that lives on a server, and offline they can only refuse. Others
 * are a view onto a CALCULATION, and the calculation can travel: the Academy's almanac is VSOP87
 * series, so a phone at sea computes the sky rather than asking for it. Those two cases must not be
 * treated alike, and only the caller knows which it has — hence a hook here rather than a policy.
 *
 * It runs ONLY when the fetch itself failed (no network), never to paper over an HTTP error: a 500
 * from a live server is a fault to surface, not an excuse to substitute a local answer.
 *
 * The result carries `computedLocally: true`. A locally computed value must never be laundered into
 * looking like it came from the server — the provenance of a figure is part of the figure (FP-018).
 * Pass a STABLE function; it is deliberately not in the effect's dependency list.
 */
/**
 * ── `headers`: WHO IS ASKING (added 2026-08-16, and it was overdue) ───────────────────────────
 * This hook sent a content-type and nothing else, so every call it made was ANONYMOUS. For a fleet
 * widget that is correct and for a gated one it is a silent, total failure: the Academy's learner
 * signed in, the token was stored, and every chapter he had paid for went on refusing him, because
 * the request that asked for the chapter never carried the identity. He reported it as "it logs and
 * throws out", which is exactly what it looks like from outside — Progress knew him (that call sent
 * its own headers by hand) and the chapters did not.
 *
 * Optional and merged, so a caller that passes nothing behaves exactly as before. Keyed by value in
 * the dependency list rather than by reference, because `authHeaders()` returns a fresh object on
 * every render and putting it in deps directly is an infinite fetch loop.
 */
export function useWidgetData(widget, vars = {}, { baseUrl = '', plan, enabled = true, offline, headers } = {}) {
  const [state, setState] = useState({ loading: enabled, data: null, envelope: null });
  const varsKey = JSON.stringify(vars);
  const headersKey = JSON.stringify(headers || null);
  const seq = useRef(0);

  useEffect(() => {
    if (!enabled || !widget) { setState({ loading: false, data: null, envelope: null }); return; }
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true }));

    (async () => {
      const p = plan ? plan(widget, vars, { baseUrl }) : null;
      if (!p) { if (seq.current === mine) setState({ loading: false, data: null,
        envelope: { state: STATES.FAILED, reason: 'no plan() supplied to useWidgetData' } }); return; }

      // plan() returns a refusal envelope for a widget that is declared but not wired. That is a
      // fact about the system, not an error — it renders as a refusal like any other.
      if (p.callable !== true) { if (seq.current === mine)
        setState({ loading: false, data: null, envelope: p }); return; }

      try {
        const res = await fetch(p.url, {
          method: p.method,
          headers: { ...(p.body ? { 'content-type': 'application/json' } : null), ...(headers || null) },
          body: p.body ? JSON.stringify(p.body) : undefined,
        });
        // A body-shaped 200 from the wrong backend is the failure no status code can see, so the
        // BODY decides, not res.ok alone.
        const json = await res.json().catch(() => null);
        /* 402 IS NOT AN OUTAGE. Falling into the generic !res.ok branch below rendered a paywall as
           "failed — HTTP 402", which reads as a bug the product should fix rather than a door the
           learner can open. It is lifted out here so the body's own words (what is gated, and what
           is still free) survive all the way to the surface. */
        if (res.status === 402) {
          setState({ loading: false, data: null,
            envelope: { state: STATES.GATED,
                        reason: json?.error || 'this is for enrolled learners',
                        free_instead: json?.free_instead || null,
                        track: json?.track || null, chapter: json?.chapter ?? null,
                        title: json?.title || null, named: json?.named ?? null,
                        /* Whether the refused thing costs money. A gate that cannot tell a free
                           chapter from a paid one makes the surface say "enrol" in one voice for
                           both, and a learner reads that as a price. Passed through rather than
                           inferred — only the server knows what is being sold. */
                        free_chapter: json?.free_chapter ?? false } });
          return;
        }
        if (seq.current !== mine) return;
        if (!res.ok || json === null) {
          setState({ loading: false, data: null,
            envelope: { state: STATES.FAILED, reason: `HTTP ${res.status} from ${p.url}` } });
          return;
        }
        if (import.meta.env?.DEV) {
          const faults = validate(json.state ? json : { state: STATES.COMPUTED, value: json });
          if (faults.length) console.warn('[glass] provenance faults:', faults, p.url);
        }
        const payload = p.kind === 'graphql' ? json?.data?.[p.field] ?? json?.data : json;
        setState({ loading: false, data: payload, envelope: null });
      } catch (err) {
        if (seq.current !== mine) return;
        // The network is gone. If this widget can compute itself, it does — and says so.
        if (typeof offline === 'function') {
          try {
            const local = offline(vars);
            if (local != null) {
              if (seq.current !== mine) return;
              setState({ loading: false, data: local, envelope: null, computedLocally: true });
              return;
            }
          } catch { /* a failed local computation is not an answer — fall through and refuse */ }
        }
        // Offline is FAILED, not empty. An empty table would say "there is nothing", which is a
        // different and untrue statement.
        setState({ loading: false, data: null,
          envelope: { state: STATES.FAILED, reason: String(err?.message || err) } });
      }
    })();
  }, [widget, varsKey, baseUrl, enabled, plan, headersKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}
